import React, { useContext, useState, useEffect } from 'react';
import { Eye, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import taskService from '../../../../services/taskService';
import Pagination from '../../../../components/Pagination';

export default function AllOrganisationTask() {
  const {user,hasRole, authTokens } = useContext(AuthContext);
  const { showError } = useToast();
  const navigate = useNavigate();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  useEffect(() => {
    if(canViewAllTasks()){
      fetchOrgTasks();
    }
  }, [pagination.page, pagination.page_size]);

  const fetchOrgTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const pagination_data = {
        page: pagination.page,
        page_size: pagination.page_size,
      };
      const result = await taskService.getOrganizationTasks(authTokens.access, pagination_data);
      if (result.success) {
        setTasks(result.data.results);

        setPagination(prev => ({
          ...prev,
          count: result.data.count,
          total_pages: result.data.total_pages
        }));
      } else {
        setError(result.error);
        showError(result.error || 'Erreur lors du chargement des tâches de l\'organisation');
      }
    } catch (err) {
      setError('Erreur de connexion');
      showError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1 // Reset to first page
    }));
  };

  const canViewAllTasks = () => {
    return hasRole("Admin") || hasRole("RiskManager");
  };

  const handleViewDetails = (taskId) => {
    navigate(`/dashboard/tasks/${taskId}`);
  };



  const canViewHistory = () => {
    return hasRole("Admin");
  }

  const handleViewHistory = (task) => {
    navigate(`/dashboard/admin/detailedLogs/process-task/${task.id}`);
    // navigate(`/dashboard/admin/logs/risk-details/${risk.id}`, { state: { risk } });
  };

  const getStatusBadge = (status) => {
    const statusInfo = taskService.getStatusInfo(status);
    return `px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.textColor}`;
  };

  const getStatusLabel = (status) => {
    const statusInfo = taskService.getStatusInfo(status);
    return statusInfo.label;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

      // If user doesn't have permission to view all processes
  if (!canViewAllTasks()) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="text-yellow-800">
          <h3 className="font-medium">Accès restreint</h3>
          <p className="text-sm mt-1">
           Désolé, vous n'avez pas accès aux données de cette page
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Toutes les Tâches de l'Organisation</h2>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button onClick={fetchOrgTasks} className="mt-2 text-red-600 hover:text-red-800 underline">Réessayer</button>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom de la Tâche</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigné à</th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processus</th> */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date d'échéance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tasks.map((task) => (
                <tr key={task.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{task.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.assigned_to_details ? `${task.assigned_to_details.first_name} ${task.assigned_to_details.last_name}` : 'N/A'}</td>
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{task.process_details?.process_name || 'N/A'}</td> */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(task.risk_action_details?.dueDate)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(task.status)}>{getStatusLabel(task.status)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button onClick={() => handleViewDetails(task.id)} className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded" title="Voir les détails">
                      <Eye className="h-4 w-4" />
                    </button>
                      {canViewHistory() && (
                        <button 
                          onClick={() => handleViewHistory(task)}
                          className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                          title="Afficher l'historique "
                        >
                          <History className="h-4 w-4" />
                        </button> 
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          totalItems={pagination.count}
          pageSize={pagination.page_size}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          itemName="Tasks" 
        />
        {tasks.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-lg font-medium text-gray-500">Aucune tâche n'a été trouvée dans l'organisation.</p>
          </div>
        )}
      </div>
    </div>
  );
}
